import { components } from "@/api-schema";
import {
  PathsV1VehicleValuationSearchPostParametersQueryOrderByDirection,
  PathsV1VehicleValuationSearchPostParametersQueryOrderByField,
  PathsV1VehicleValuationUuidGetParametersQueryExtend,
} from "@/api-schema-enums";
import type {
  VehicleValuationExtend,
  VehicleValuationSearchBody,
  VehicleValuationSearchParameters,
} from "@/common/api/vehicle-valuation/use-vehicle-valuation-search.query";
import {
  useVehicleValuationSearchQuery,
  VEHICLE_VALUATION_RESOURCE,
} from "@/common/api/vehicle-valuation/use-vehicle-valuation-search.query";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures } from "@/components/data-table/types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "../_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "../_components/base-predefined-table.types";
import {
  buildVehicleValuationTableColumns,
  vehicleValuationTableDefaultColumns,
} from "./vehicle-valuation-table-columns";

type VehicleValuationRow = components["schemas"]["VehicleValuation"] & RowWithIdentifier;
type VehicleValuationSorting = NonNullable<VehicleValuationSearchParameters["orderBy"]>;

const VEHICLE_VALUATION_PAGE_SIZE = 20;

const DEFAULT_EXTEND: PathsV1VehicleValuationUuidGetParametersQueryExtend[] = [
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle,
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration,
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily,
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily_Brand,
];

const VEHICLE_VALUATION_TABLE_CONFIG: PredefinedTableConfig<VehicleValuationSorting> = {
  defaultPageSize: VEHICLE_VALUATION_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1VehicleValuationSearchPostParametersQueryOrderByDirection.Desc,
      field: PathsV1VehicleValuationSearchPostParametersQueryOrderByField.CreatedAt,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: "fab:SearchVehicleValuation",
    features: {
      multiSelection: true,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: false,
    showDelete: false,
    showView: true,
    viewLabel: "fab:ViewVehicleValuation",
  } as const,
  sortingMapping: {
    brand: ["vehicle.modelGeneration.modelFamily.brand"],
    vehicleTitle: [
      "vehicle.modelGeneration.modelFamily.brand",
      "vehicle.modelGeneration.modelFamily",
      "vehicle.modelGeneration",
    ],
  } as const,
} as const;

const VEHICLE_VALUATION_TABLE_QUERY_HOOKS: DataFetchHooks<
  VehicleValuationRow,
  VehicleValuationSearchParameters,
  VehicleValuationSearchBody
> = {
  useSearchQuery: useVehicleValuationSearchQuery,
} as const;

export interface VehicleValuationTableProps extends BasePredefinedTableProps<
  VehicleValuationRow,
  VehicleValuationSorting,
  VehicleValuationSearchParameters,
  VehicleValuationSearchBody
> {
  extend?: ReadonlyArray<VehicleValuationExtend>;
  canManageVehicleValuation?: boolean;
}

const BaseVehicleValuationTable = createBasePredefinedTable({
  config: VEHICLE_VALUATION_TABLE_CONFIG,
  queryHooks: VEHICLE_VALUATION_TABLE_QUERY_HOOKS,
  defaultColumns: vehicleValuationTableDefaultColumns,
});

export function VehicleValuationTable(props: VehicleValuationTableProps) {
  const { t } = useTranslation(["fab"]);
  const {
    name = `${VEHICLE_VALUATION_RESOURCE}-table`,
    extend = DEFAULT_EXTEND,
    canManageVehicleValuation,
    ...restProps
  } = props;

  const columns = useMemo(
    () => buildVehicleValuationTableColumns(canManageVehicleValuation ?? false),
    [canManageVehicleValuation],
  );

  return (
    <BaseVehicleValuationTable
      searchPlaceholder={t("fab:SearchVehicleValuation")}
      name={name}
      extend={extend}
      columns={columns}
      {...restProps}
    />
  );
}
